import {render} from './ToastMessage';

const SELECTOR_DROPZONE          = '[data-component="upload-dropzone"]';
const SELECTOR_PROGRESS          = '[data-component="upload-progress"]';
const SELECTOR_PROGRESS_VALUE    = '[data-progress-value]';
const SELECTOR_PROGRESS_BAR      = '[data-progress-bar]';
const SELECTOR_FILE_INPUT        = 'input[type="file"]';
const SELECTOR_SELECTED_NAME     = '[data-selected-file-name]';
const SELECTOR_TEMPLATE_DROPZONE = '[data-component="template-dropzone"]';
const SELECTOR_SIZED_FILE_INPUT  = 'input[type="file"][data-maximum-bytes]';

const BYTES_PER_MEGABYTE = 1_048_576;

// SVG <circle r="28"> in the upload-progress template.
const PROGRESS_CIRCUMFERENCE = 2 * Math.PI * 28;

const findDropzone = eventTarget =>
    eventTarget?.closest?.(`${SELECTOR_DROPZONE}, ${SELECTOR_TEMPLATE_DROPZONE}`) ?? null;

const setProgressValue = (form, percent) => {
    const progressElement = form.querySelector(SELECTOR_PROGRESS);

    if (!progressElement) {
        return;
    }

    const progressValue = progressElement.querySelector(SELECTOR_PROGRESS_VALUE);
    const progressBar   = progressElement.querySelector(SELECTOR_PROGRESS_BAR);

    if (progressValue) {
        progressValue.textContent = `${Math.round(percent)}%`;
    }

    if (progressBar) {
        const offset = PROGRESS_CIRCUMFERENCE * (1 - percent / 100);
        progressBar.style.strokeDasharray = `${PROGRESS_CIRCUMFERENCE}`;
        progressBar.style.strokeDashoffset = `${offset}`;
    }
};

// Prevent the browser from opening files dropped outside any dropzone.
export const preventStrayFileDrop = event => {
    if (event.dataTransfer?.types?.includes('Files')) {
        event.preventDefault();
    }
};

export const highlightDropzoneOnDragEnter = event => {
    const dropzone = findDropzone(event.target);

    if (!dropzone) {
        return;
    }

    event.preventDefault();
    dropzone.classList.add('is-dragover');
};

export const clearDropzoneOnDragLeave = event => {
    const dropzone = findDropzone(event.target);

    if (!dropzone || dropzone.contains(event.relatedTarget)) {
        return;
    }

    dropzone.classList.remove('is-dragover');
};

export const acceptDroppedFile = event => {
    const dropzone = findDropzone(event.target);

    if (!dropzone) {
        event.preventDefault();

        return;
    }

    event.preventDefault();
    dropzone.classList.remove('is-dragover');

    const input = dropzone.querySelector(SELECTOR_FILE_INPUT);
    const files = event.dataTransfer?.files;

    if (!input || !files || files.length === 0) {
        return;
    }

    input.files = files;
    input.dispatchEvent(new Event('change', {bubbles: true}));
};

const isOversized = input => Array.from(input.files).some(file => file.size > Number(input.dataset.maximumBytes));

const refuse = input => {
    const template = document.getElementById('file-too-large-toast-message').cloneNode(true);

    template.content.querySelector('b').textContent = Math.floor(Number(input.dataset.maximumBytes) / BYTES_PER_MEGABYTE);
    render(template);
    input.value = '';
};

export const refuseOversizedFile = input => {
    if (!(input instanceof HTMLInputElement) || !input.matches(SELECTOR_SIZED_FILE_INPUT) || !isOversized(input)) {
        return;
    }

    refuse(input);
};

// An input posting through hx-post on change sends before the document change listener runs.
export const cancelOversizedUpload = event => {
    const element   = event.detail.elt;
    const inputs    = element.matches(SELECTOR_SIZED_FILE_INPUT)
        ? [element]
        : Array.from(element.querySelectorAll(SELECTOR_SIZED_FILE_INPUT));
    const oversized = inputs.find(isOversized);

    if (!oversized) {
        return;
    }

    event.preventDefault();
    refuse(oversized);
};

// Picking or dropping a file submits the upload form immediately, unless the dropzone
// sits in a form with further fields and opts out with data-auto-submit="false".
export const submitOnFileSelect = input => {
    if (!(input instanceof HTMLInputElement) || input.type !== 'file') {
        return;
    }

    const dropzone = findDropzone(input);

    if (!dropzone || input.files.length === 0 || dropzone.dataset.autoSubmit === 'false') {
        return;
    }

    input.closest('form')?.requestSubmit();
};

// A dropzone that does not auto-submit has no server round trip to name the file,
// so it names it here instead.
export const showSelectedFileName = input => {
    if (!(input instanceof HTMLInputElement) || input.type !== 'file') {
        return;
    }

    const dropzone = findDropzone(input);
    const target   = dropzone?.querySelector(SELECTOR_SELECTED_NAME);

    if (!target) {
        return;
    }

    const [file] = input.files;

    target.textContent = file?.name ?? '';
    dropzone.classList.toggle('has-file', file !== undefined);
};

export const updateUploadProgress = event => {
    if (!event.target.matches?.('form')) {
        return;
    }

    const {loaded, total} = event.detail ?? {};

    if (!total) {
        return;
    }

    setProgressValue(event.target, (loaded / total) * 100);
};

// SVG <circle r="13"> in the template dropzone progress markup.
const TEMPLATE_PROGRESS_CIRCUMFERENCE = 2 * Math.PI * 13;

const setTemplateProgressValue = (dropzone, percent) => {
    const progressValue = dropzone.querySelector(SELECTOR_PROGRESS_VALUE);
    const progressBar   = dropzone.querySelector(SELECTOR_PROGRESS_BAR);

    progressValue.textContent = `${Math.round(percent)}%`;
    progressBar.style.strokeDasharray = `${TEMPLATE_PROGRESS_CIRCUMFERENCE}`;
    progressBar.style.strokeDashoffset = `${TEMPLATE_PROGRESS_CIRCUMFERENCE * (1 - percent / 100)}`;
};

export const updateTemplateUploadProgress = event => {
    if (!event.target.matches?.('.template-dropzone-form')) {
        return;
    }

    const dropzone = event.target.querySelector(SELECTOR_TEMPLATE_DROPZONE);

    if (!dropzone) {
        return;
    }

    const {loaded, total} = event.detail ?? {};

    if (!total) {
        return;
    }

    setTemplateProgressValue(dropzone, (loaded / total) * 100);
};

const clearFileInput = dropzone => {
    const input = dropzone.querySelector(SELECTOR_FILE_INPUT);

    if (input) {
        input.value = '';
    }
};

export const prepareUpload = event => {
    const form = event.target;

    if (!form.matches?.('form')) {
        return;
    }

    const uploadDropzone = form.querySelector(SELECTOR_DROPZONE);

    if (uploadDropzone) {
        setProgressValue(form, 0);
        clearFileInput(uploadDropzone);
    }

    const templateDropzone = form.querySelector(SELECTOR_TEMPLATE_DROPZONE);

    if (templateDropzone) {
        setTemplateProgressValue(templateDropzone, 0);
        clearFileInput(templateDropzone);
    }
};
